import { api } from "../../lib";

export interface CommunicationBypassPrefix {
  id: string;
  code: string;
  color: string | null;
  isActive: boolean;
}

export interface CommunicationSettingsUpdater {
  id: string;
  name: string;
}

export interface StaticEmailExemptions {
  addresses: string[];
  domains: string[];
}

export interface CommunicationRedirectSettings {
  emailRedirectEnabled: boolean;
  faxRedirectEnabled: boolean;
  selectedPrefixes: CommunicationBypassPrefix[];
  availablePrefixes: CommunicationBypassPrefix[];
  revision: number;
  updatedAt: string | null;
  updatedBy: CommunicationSettingsUpdater | null;
  staticEmailExemptions: StaticEmailExemptions;
}

export interface UpdateCommunicationRedirectSettingsRequest {
  prefixIds: string[];
  expectedRevision: number;
}

interface HttpError {
  response?: {
    status?: number;
    data?: { code?: string; currentRevision?: number };
  };
}

export class CommunicationSettingsConflictError extends Error {
  readonly currentRevision?: number;

  constructor(currentRevision?: number) {
    super(
      "These settings changed while you were editing. Reload the latest version."
    );
    this.name = "CommunicationSettingsConflictError";
    this.currentRevision = currentRevision;
  }
}

function translateSettingsError(error: unknown): Error {
  const response = (error as HttpError).response;
  if (response?.status === 409) {
    return new CommunicationSettingsConflictError(
      response.data?.currentRevision
    );
  }
  if (response?.status === 403) {
    return new Error(
      "You do not have permission to manage redirect bypass prefixes."
    );
  }
  return new Error(
    "Communication redirect settings are unavailable. Please try again."
  );
}

const SETTINGS_PATH = "/communication-settings/redirect-bypass-prefixes";

export const communicationSettingsService = {
  getRedirectBypassPrefixes:
    async (): Promise<CommunicationRedirectSettings> => {
      try {
        return await api.get<CommunicationRedirectSettings>(SETTINGS_PATH);
      } catch (error) {
        throw translateSettingsError(error);
      }
    },

  updateRedirectBypassPrefixes: async (
    request: UpdateCommunicationRedirectSettingsRequest
  ): Promise<CommunicationRedirectSettings> => {
    try {
      return await api.put<CommunicationRedirectSettings>(
        SETTINGS_PATH,
        request
      );
    } catch (error) {
      throw translateSettingsError(error);
    }
  },
};
